/**
 * The accepted-state shape gate: the structural half of what Nest may commit,
 * compile, launch or call ready. Readiness, compile and the write gate run it
 * first, so a change here changes what is refused.
 *
 * It is deliberately stricter than the draft's owner. The Pydantic models in
 * `apps/python-api/models/questionnaire/contracts.py` (from which
 * `draft.generated.ts` is generated) are readers: they take a stored draft of
 * any age, so they default fields older drafts lack and drop retired keys
 * before validating. This gate holds what is written now:
 *   - respondent-facing and identity strings are non-blank;
 *   - fields every current writer writes are required here, though the owner
 *     defaults them for older drafts (design brief, study goals, reporting
 *     plan slots, the compiled config's design and exposure zones);
 *   - the draft root and the metric plan are closed (the owner ignores
 *     unknown root keys); the retired keys a stored draft may still carry are
 *     listed where they are tolerated;
 *   - rules the owner does not state (response kind equals question type, the
 *     exact-subject attribution, the complete Van Westendorp roster, the
 *     product price, which this module owns).
 * It is never looser than the owner: a value the owner refuses is refused
 * here too, or a committed draft would fail PLAN, REPLAN or analysis on its
 * shape. (The StudySpec's shape is checked against its own schema beside this
 * gate, in Nest's `accepted-structure.ts`.) Every difference from the owner is
 * listed with its reason in
 * `tests/apps/python-api/test_accepted_artifact_validator_parity.py`; a field
 * added to or changed in the owner fails that test until it is placed here.
 */
import { worded } from "./rule-catalog";
import type { QuestionnaireValidationIssue } from "./index";
import {
  CONTINUOUS_UNIT_IDS,
  PRODUCT_PRICE_CURRENCIES,
  QUESTION_TYPE_IDS,
  RATING_POLARITIES,
  RATING_UNIT_IDS,
  TESTED_ASSET_TYPE_IDS,
} from "./registry";
import { ASSET_MEDIA_REF_STRING_FIELDS } from "./asset-media-locator";
import { isShownAssetFamily } from "./asset-families";
import { CONDITION_OPERATORS } from "./condition-operators";
import { CANONICAL_SLIDE_TYPES, METRIC_KINDS } from "./canonical-vocabularies";
import { isStudyEvidenceContract } from "./study-evidence-validator";
import { REPORT_CARD_SHAPES } from "./report-card-capabilities";
import {
  AD_PLACEMENTS,
  BILLING_PERIODS,
  DURATION_UNITS,
  MESSAGE_CLAIM_TYPES,
  STUDY_BRAND_SOURCES,
} from "./vocabularies";
import { STUDY_SPEC_SCHEMA_VERSION } from "./study-spec";
import type * as Owner from "./draft.generated";

type JsonRecord = Record<string, unknown>;
type Fields = Record<string, Schema>;
type Schema =
  | { kind: "string"; nonBlank?: boolean }
  | { kind: "number"; integer?: boolean }
  | { kind: "boolean" }
  | { kind: "literal"; value: unknown }
  | { kind: "enum"; values: ReadonlyArray<unknown> }
  | { kind: "array"; item: Schema }
  | {
      kind: "object";
      required: Fields;
      optional?: Fields;
      allowUnknown?: boolean;
    }
  | {
      kind: "discriminated";
      field: string;
      variants: Record<string, Schema>;
    }
  | { kind: "record"; value: Schema }
  // An optional field whose null is not a value: the owner refuses it.
  | { kind: "present"; schema: Schema }
  // A value that may be null; any other value is checked by `schema`.
  | { kind: "nullable"; schema: Schema }
  // `schema`, then a rule across its fields once it holds.
  | {
      kind: "refined";
      schema: Schema;
      check: (value: unknown, path: string, result: ContractResult) => void;
    }
  // One of the owner's union members, named by its generated type and chosen
  // by `select` from the value's own typed field.
  | {
      kind: "union";
      members: Record<string, Schema>;
      select: (value: unknown) => string;
    }
  | { kind: "unknown" }
  | {
      kind: "custom";
      validate: (value: unknown, path: string, result: ContractResult) => void;
    };

interface ContractResult {
  issues: Array<QuestionnaireValidationIssue>;
}

const s = {
  string: (nonBlank = false): Schema => ({ kind: "string", nonBlank }),
  number: (integer = false): Schema => ({ kind: "number", integer }),
  boolean: { kind: "boolean" } as Schema,
  literal: (value: unknown): Schema => ({ kind: "literal", value }),
  enum: (values: ReadonlyArray<unknown>): Schema => ({ kind: "enum", values }),
  array: (item: Schema): Schema => ({ kind: "array", item }),
  object: (
    required: Fields,
    optional: Fields = {},
    allowUnknown = false,
  ): Schema => ({ kind: "object", required, optional, allowUnknown }),
  discriminated: (field: string, variants: Record<string, Schema>): Schema => ({
    kind: "discriminated",
    field,
    variants,
  }),
  record: (value: Schema): Schema => ({ kind: "record", value }),
  present: (schema: Schema): Schema => ({ kind: "present", schema }),
  refine: (
    schema: Schema,
    check: (value: unknown, path: string, result: ContractResult) => void,
  ): Schema => ({ kind: "refined", schema, check }),
  union: (
    members: Record<string, Schema>,
    select: (value: unknown) => string,
  ): Schema => ({ kind: "union", members, select }),
  unknown: { kind: "unknown" } as Schema,
  custom: (
    validate: (value: unknown, path: string, result: ContractResult) => void,
  ): Schema => ({ kind: "custom", validate }),
};

const nonBlank = s.string(true);
const strings = s.array(nonBlank);

type OwnerKeys<T> = Extract<keyof T, string>;
type OwnerRules<Keys extends string> = { [K in Keys]?: Schema };
type Only<R, Keys extends string> = R & Record<Exclude<keyof R, Keys>, never>;
type Unplaced<Missing> = [Missing] extends [never] ? [] : [unplacedOwnerFields: Missing];

/**
 * An object rule written against its owner's generated type `T`
 * (`draft.generated.ts`). Only the owner's fields, and the `Retired` keys a
 * stored value may still carry, may be placed; every owner field must be
 * placed, required or optional, unless it is named in `Refused` (a field this
 * gate refuses on purpose). A field the owner adds, drops or renames fails
 * type-checking here: the call then asks for an `unplacedOwnerFields` argument
 * naming what is missing. Being stricter than the owner (requiring an optional
 * field, closing an open one) is the gate's to decide and stays allowed; the
 * parity test lists each such difference with its reason.
 */
function objectOf<T, Retired extends string = never, Refused extends OwnerKeys<T> = never>() {
  return <
    R extends OwnerRules<OwnerKeys<T> | Retired>,
    O extends OwnerRules<OwnerKeys<T> | Retired> = {},
  >(
    required: Only<R, OwnerKeys<T> | Retired>,
    optional?: Only<O, OwnerKeys<T> | Retired>,
    ...unplaced: Unplaced<Exclude<OwnerKeys<T>, keyof R | keyof O | Refused>>
  ): Schema => s.object(required, optional ?? {});
}
/** `ProductAssetPrice`: a finite amount and a non-blank currency code. */
// A product's typed price (F18 cross review, the one owner of a valid price):
// an amount above 0, in a currency the builder and the report print alike.
const productPrice = objectOf<Owner.ProductAssetPrice>()({
  amount: s.custom((value, path, result) => {
    if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
      fail(result, path, "Expected an amount above 0.");
    }
  }),
  currencyCode: s.custom((value, path, result) => {
    if (
      typeof value !== "string" ||
      !(PRODUCT_PRICE_CURRENCIES as ReadonlyArray<string>).includes(value.trim().toUpperCase())
    ) {
      fail(result, path, `Expected one of the supported currency codes: ${PRODUCT_PRICE_CURRENCIES.join(", ")}.`);
    }
  }),
}, {
  // A subscription's billing period (decision 28); absent is no period, and
  // a null is refused, so every writer stores the one form.
  billingPeriod: s.present(s.enum(BILLING_PERIODS)),
});
const nullable = (schema: Schema): Schema => ({ kind: "nullable", schema });

function child(path: string, field: string): string {
  return path ? `${path}.${field}` : field;
}

function fail(result: ContractResult, path: string, message: string): void {
  result.issues.push({
    path,
    code: "accepted_artifact_contract_invalid",
    message: worded("accepted_artifact_contract_invalid", message),
    severity: "error",
  });
}

function isRecord(value: unknown): value is JsonRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function validate(
  schema: Schema,
  value: unknown,
  path: string,
  result: ContractResult,
): void {
  switch (schema.kind) {
    case "unknown":
      return;
    case "string":
      if (
        typeof value !== "string" ||
        (schema.nonBlank && value.trim().length === 0)
      ) {
        fail(result, path, "Expected a non-blank string.");
      }
      return;
    case "number":
      if (
        typeof value !== "number" ||
        !Number.isFinite(value) ||
        (schema.integer && !Number.isInteger(value))
      ) {
        fail(result, path, "Expected a finite number of the declared shape.");
      }
      return;
    case "boolean":
      if (typeof value !== "boolean") fail(result, path, "Expected a boolean.");
      return;
    case "literal":
      if (value !== schema.value) {
        fail(result, path, `Expected ${JSON.stringify(schema.value)}.`);
      }
      return;
    case "enum":
      if (!schema.values.includes(value)) {
        fail(result, path, "Value is outside the closed contract vocabulary.");
      }
      return;
    case "array":
      if (!Array.isArray(value)) {
        fail(result, path, "Expected an array.");
        return;
      }
      value.forEach((entry, index) =>
        validate(schema.item, entry, `${path}[${index}]`, result),
      );
      return;
    case "record":
      if (!isRecord(value)) {
        fail(result, path, "Expected an object.");
        return;
      }
      Object.entries(value).forEach(([key, entry]) =>
        validate(schema.value, entry, child(path, key), result),
      );
      return;
    case "object": {
      if (!isRecord(value)) {
        fail(result, path, "Expected an object.");
        return;
      }
      const allowed = new Set([
        ...Object.keys(schema.required),
        ...Object.keys(schema.optional ?? {}),
      ]);
      if (!schema.allowUnknown) {
        Object.keys(value).forEach((key) => {
          if (!allowed.has(key)) {
            fail(
              result,
              child(path, key),
              "Unexpected field in accepted artifact.",
            );
          }
        });
      }
      Object.entries(schema.required).forEach(([key, fieldSchema]) => {
        if (!(key in value)) {
          fail(result, child(path, key), "Required field is missing.");
        } else {
          validate(fieldSchema, value[key], child(path, key), result);
        }
      });
      Object.entries(schema.optional ?? {}).forEach(([key, fieldSchema]) => {
        // Runtime objects often carry `undefined` optional properties which
        // disappear on JSON serialization. Treat them exactly like omission;
        // null remains an explicit compatibility value and is handled by the
        // nullable wrapper.
        if (key in value && value[key] !== undefined) {
          validate(
            fieldSchema.kind === "present" ? fieldSchema : nullable(fieldSchema),
            value[key],
            child(path, key),
            result,
          );
        }
      });
      return;
    }
    case "discriminated": {
      if (!isRecord(value)) {
        fail(result, path, "Expected an object.");
        return;
      }
      const discriminator = value[schema.field];
      const variant =
        typeof discriminator === "string"
          ? schema.variants[discriminator]
          : undefined;
      if (!variant) {
        fail(
          result,
          child(path, schema.field),
          "Value is outside the closed contract vocabulary.",
        );
        return;
      }
      validate(variant, value, path, result);
      return;
    }
    case "present":
      validate(schema.schema, value, path, result);
      return;
    case "nullable":
      if (value !== null) validate(schema.schema, value, path, result);
      return;
    case "refined": {
      const start = result.issues.length;
      validate(schema.schema, value, path, result);
      if (result.issues.length === start) schema.check(value, path, result);
      return;
    }
    case "union":
      validate(schema.members[schema.select(value)], value, path, result);
      return;
    case "custom":
      schema.validate(value, path, result);
  }
}

const questionTypes = [...QUESTION_TYPE_IDS];
const testedAssetTypes = [...TESTED_ASSET_TYPE_IDS];
const unitIds = [...RATING_UNIT_IDS, ...CONTINUOUS_UNIT_IDS];

const assetMentionRef = objectOf<Owner.AssetMentionRef>()(
  {
    assetId: nonBlank,
    assetStableKey: nonBlank,
    assetType: s.enum(testedAssetTypes),
    label: nonBlank,
    role: s.enum(["stimulus", "matrix_row", "comparison", "choice"]),
    source: s.enum(["mention", "matrix_row", "manual", "compiler"]),
  },
  { mentionResolved: s.boolean },
);

const choice = objectOf<Owner.ChoiceOption>()(
  { id: nonBlank, value: nonBlank, label: nonBlank },
  {
    description: s.string(),
    exclusive: s.boolean,
    keepLast: s.boolean,
    assetRefs: s.array(assetMentionRef),
    brandRole: s.enum(["focal", "competitor"]),
  },
);
const choicesFrom = objectOf<Owner.ChoicesFromRef>()({
  sourceStableKey: nonBlank,
  mode: s.enum(["selected", "unselected", "all"]),
});
const optionSource = objectOf<Owner.TestedAssetsOptionSource>()({
  kind: s.literal("tested_assets"),
  assetType: s.enum(testedAssetTypes),
});
// A select or ranking list's source: a tested-asset family or the brand roster (C13-1).
const listOptionSource = s.discriminated("kind", {
  tested_assets: optionSource,
  brand_roster: objectOf<Owner.BrandRosterOptionSource>()({
    kind: s.literal("brand_roster"),
  }),
});
const anchors = objectOf<Owner.RatingAnchors, "higherIsBetter">()(
  // Endpoints describe a numeric scale when present; an explicit blank clears
  // the optional annotation without changing the scale's typed unit.
  { minLabel: s.string(), maxLabel: s.string() },
  {
    midpointLabel: s.string(),
    // The owner defaults an absent polarity; it refuses a null one.
    polarity: s.present(s.enum(RATING_POLARITIES)),
    // Retired boolean polarity, undeclared on `RatingAnchors`: tolerated on a
    // draft stored before the normaliser converted it on write.
    higherIsBetter: s.unknown,
  },
);
const matrixRow = objectOf<Owner.MatrixRow>()(
  { id: nonBlank, value: nonBlank, label: nonBlank },
  {
    description: s.string(),
    assetRefs: s.array(assetMentionRef),
    brandRole: s.enum(["focal", "competitor"]),
  },
);
const matrixRows = s.discriminated("source", {
  fixed: objectOf<Owner.FixedMatrixRows>()(
    { source: s.literal("fixed"), rows: s.array(matrixRow) },
    { derivedFrom: s.enum(["brand_roster"]) },
  ),
  tested_assets: objectOf<Owner.TestedAssetsMatrixRows>()({
    source: s.literal("tested_assets"),
    assetType: s.enum(testedAssetTypes),
  }),
});
const matrixMeasure = objectOf<Owner.MatrixRatingMeasure, "higherIsBetter">()(
  {
    id: nonBlank,
    label: nonBlank,
    unit: s.enum(RATING_UNIT_IDS),
    anchors,
    includeNotApplicable: s.boolean,
  },
  {
    pointLabels: s.array(s.string()),
    notApplicableLabel: s.string(),
    // Retired measure-level mirror of its anchors' polarity, undeclared on
    // `MatrixRatingMeasure`: tolerated on a draft stored before the
    // normaliser converted it on write.
    higherIsBetter: s.unknown,
  },
);

const assetQuestionBinding = objectOf<Owner.AssetQuestionBinding>()(
  {
    mode: s.enum(["none", "all_assets", "per_asset"]),
    assetTypes: s.array(s.enum(testedAssetTypes)),
  },
  { rowSource: s.literal("tested_assets"), view: s.literal("recalled") },
);

const responses = s.discriminated("kind", {
  single_select: objectOf<Owner.SingleSelectResponse>()(
    { kind: s.literal("single_select"), options: s.array(choice) },
    {
      randomizeOptions: s.present(s.boolean),
      choicesFrom,
      optionSource: listOptionSource,
      ordered: s.present(s.boolean),
    },
  ),
  multi_select: objectOf<Owner.MultiSelectResponse>()(
    { kind: s.literal("multi_select"), options: s.array(choice) },
    {
      minSelected: s.number(true),
      maxSelected: s.number(true),
      randomizeOptions: s.present(s.boolean),
      choicesFrom,
      optionSource: listOptionSource,
      ordered: s.present(s.boolean),
    },
  ),
  rating: objectOf<Owner.RatingResponse>()(
    {
      kind: s.literal("rating"),
      unit: s.enum(RATING_UNIT_IDS),
      anchors,
      includeNotApplicable: s.boolean,
    },
    { pointLabels: s.array(s.string()), notApplicableLabel: s.string() },
  ),
  continuous: objectOf<Owner.ContinuousResponse>()(
    { kind: s.literal("continuous"), unit: s.enum(CONTINUOUS_UNIT_IDS) },
    {
      min: s.number(),
      max: s.number(),
      precision: s.number(),
      currencyCode: s.string(),
      durationUnit: s.enum(DURATION_UNITS),
    },
  ),
  open_end: objectOf<Owner.OpenEndResponse, "neutralAnswerInstruction">()(
    { kind: s.literal("open_end"), followUpToQuestionIds: strings },
    {
      maxLength: s.number(true),
      placeholder: s.string(),
      // Retired (C02-1, owner decision D15) and undeclared on
      // `OpenEndResponse`: tolerated on a draft stored before the normaliser
      // dropped it on write; nothing reads it.
      neutralAnswerInstruction: s.unknown,
    },
  ),
  ranking: objectOf<Owner.RankingResponse>()(
    { kind: s.literal("ranking"), options: s.array(choice) },
    {
      minRankedItems: s.number(true),
      maxRankedItems: s.number(true),
      choicesFrom,
      optionSource: listOptionSource,
    },
  ),
  matrix_rating: objectOf<Owner.MatrixRatingResponse>()(
    {
      kind: s.literal("matrix_rating"),
      rows: matrixRows,
      measures: s.array(matrixMeasure),
    },
    { randomizeRows: s.present(s.boolean) },
  ),
  matrix_categorical: objectOf<Owner.MatrixCategoricalResponse>()(
    {
      kind: s.literal("matrix_categorical"),
      rows: matrixRows,
      categories: s.array(choice),
      allowNone: s.boolean,
    },
    {
      optionSource,
      ordered: s.present(s.boolean),
      randomizeRows: s.present(s.boolean),
      randomizeCategories: s.present(s.boolean),
    },
  ),
});

const customReporting = s.discriminated("kind", {
  report: objectOf<Owner.QuestionCustomReportDisposition>()(
    {
      kind: s.literal("report"),
      summaryPlacement: nullable(s.enum(["featured_question", "kpi"])),
    },
    { reportInputModuleId: nonBlank },
  ),
  survey_data_only: objectOf<Owner.QuestionCustomSurveyDataOnlyDisposition>()({
    kind: s.literal("survey_data_only"),
    reason: nonBlank,
  }),
});
const planAttribution = objectOf<Owner.QuestionPlanAttribution>()(
  {
    schemaVersion: s.enum([
      "question-plan-attribution.v1",
      "question-plan-attribution.v2",
      "question-plan-attribution.v3",
    ]),
    topicKey: nonBlank,
  },
  {
    evidenceRequirementIds: s.array(nonBlank),
    subjectAssetStableKey: nonBlank,
    recipeModuleId: nonBlank,
    customReporting,
    asked: s.literal(true),
    reportingTargetModuleId: nonBlank,
    surveyDataOnlyReason: nonBlank,
    priceRole: s.enum(["too_cheap", "cheap", "expensive", "too_expensive"]),
    openEndAnalysisFrame: s.enum(["evaluative", "factual"]),
    isPrimary: s.literal(true),
    reportLabel: nonBlank,
    cohortLabel: nonBlank,
    answeredWithout: s.array(nonBlank),
    reveals: s.array(nonBlank),
    splitGroups: s.array(
      objectOf<Owner.QuestionSplitGroup>()({ label: nonBlank, values: s.array(nonBlank) }),
    ),
  },
);

const visibilityLeaf = objectOf<Owner.QuestionVisibilityRule>()(
  {
    dependsOnStableKey: nonBlank,
    operator: s.enum(CONDITION_OPERATORS),
  },
  {
    values: strings,
    threshold: s.number(),
    thresholdHigh: s.number(),
    matrixRowValue: nonBlank,
    matrixMeasureId: nonBlank,
    rankK: s.number(true),
  },
);
const visibilityExpression = s.union(
  {
    QuestionVisibilityRuleGroup: objectOf<Owner.QuestionVisibilityRuleGroup>()({
      join: s.enum(["and", "or"]),
      rules: s.array(visibilityLeaf),
    }),
    QuestionVisibilityRule: visibilityLeaf,
  },
  (value) =>
    isRecord(value) && (value.join === "and" || value.join === "or")
      ? "QuestionVisibilityRuleGroup"
      : "QuestionVisibilityRule",
);

const question = objectOf<Owner.QuestionDefinition>()(
  {
    id: nonBlank,
    stableKey: nonBlank,
    type: s.enum(questionTypes),
    role: s.enum([
      "research",
      "asset_evaluation",
      "asset_follow_up",
      "screening",
    ]),
    title: nonBlank,
    required: s.boolean,
    response: responses,
    metricRefs: strings,
    slideRefs: strings,
    tags: strings,
    createdBy: s.enum(["chat", "user", "system"]),
    updatedAt: nonBlank,
  },
  {
    description: s.string(),
    assetBinding: assetQuestionBinding,
    assetRefs: s.array(assetMentionRef),
    inheritedFollowUpAssetContext: s.literal(true),
    planAttribution,
    awarenessEntities: strings,
    postReveal: s.boolean,
    visibilityRule: visibilityExpression,
    routingRule: objectOf<Owner.QuestionRoutingRule>()(
      {
        when: visibilityExpression,
        action: s.enum(["skip_to", "end_survey"]),
      },
      { targetStableKey: nonBlank, ruleId: nonBlank, scope: s.enum(["respondent", "session"]), disposition: s.enum(["branch_complete", "screened_out"]) },
    ),
    skipDirective: nonBlank,
    placeholderSubstitutions: s.array(
      objectOf<Owner.PlaceholderSubstitution>()({ field: s.enum(["rows", "options"]), site: nonBlank }),
    ),
  },
);

const validationIssue = objectOf<Owner.QuestionnaireValidationIssue>()(
  {
    path: s.string(),
    code: nonBlank,
    message: nonBlank,
    severity: s.enum(["error", "warning"]),
  },
  {
    // `tested_asset_unused` names its asset and the typed facts of its line.
    assetId: nonBlank,
    params: objectOf<Owner.QuestionnaireRefusalParams>()(
      {},
      {
        family: s.enum(testedAssetTypes),
        limit: s.number(true),
        count: s.number(true),
        excess: s.number(true),
        length: s.number(true),
        assetName: s.string(),
        missingFact: s.string(),
      },
    ),
  },
);
const validationState = objectOf<Owner.DraftValidationState>()(
  {
    status: s.enum(["not_validated", "valid", "invalid"]),
    issues: s.array(validationIssue),
  },
  { validatedAt: nonBlank },
);

const media = s.object(
  {
    id: nonBlank,
    kind: s.enum(["image", "video", "document", "url_snapshot"]),
  },
  {
    ...Object.fromEntries(
      ASSET_MEDIA_REF_STRING_FIELDS.map((field) => [field, s.string()]),
    ),
    sliceIndex: s.number(true),
  },
);
const assetBaseRequired = {
  id: nonBlank,
  type: s.enum(testedAssetTypes),
  stableKey: nonBlank,
  name: nonBlank,
  media: s.array(media),
  metadata: s.object({}, {}, true),
  createdAt: nonBlank,
  updatedAt: nonBlank,
} satisfies OwnerRules<OwnerKeys<Owner.PainAsset>>;
const assetBaseOptional = {
  description: s.string(),
  twinFacingLabel: s.string(),
  url: s.string(),
  stimulusStatus: s.enum(["final", "stand_in"]),
} satisfies OwnerRules<OwnerKeys<Owner.PainAsset>>;
type AssetBaseKey = keyof typeof assetBaseRequired | keyof typeof assetBaseOptional;
/** One asset family's rule, written against its generated type (see `objectOf`). */
const asset =
  <T extends { type: string }>() =>
  <R extends OwnerRules<OwnerKeys<T>> = {}, O extends OwnerRules<OwnerKeys<T>> = {}>(
    type: T["type"],
    required?: Only<R, OwnerKeys<T>>,
    optional?: Only<O, OwnerKeys<T>>,
    ...unplaced: Unplaced<Exclude<OwnerKeys<T>, AssetBaseKey | keyof R | keyof O>>
  ): Schema =>
    s.object(
      { ...assetBaseRequired, type: s.literal(type), ...(required ?? {}) },
      { ...assetBaseOptional, ...(optional ?? {}) },
    );
const testedAsset = s.discriminated("type", {
  concept: asset<Owner.ConceptAsset>()(
    "concept",
    {},
    { proposition: s.string(), targetUser: s.string() },
  ),
  ad: asset<Owner.AdAsset>()(
    "ad",
    {},
    {
      headline: s.string(),
      bodyCopy: s.string(),
      callToAction: s.string(),
      placement: s.enum(AD_PLACEMENTS),
    },
  ),
  landing_page: asset<Owner.LandingPageAsset>()(
    "landing_page",
    {},
    {
      pageUrl: s.string(),
      headline: s.string(),
      primaryCallToAction: s.string(),
      aboveFoldSummary: s.string(),
    },
  ),
  product: asset<Owner.ProductAsset>()(
    "product",
    {},
    {
      category: s.string(),
      price: productPrice,
      keyBenefits: s.present(strings),
      reasonsToBelieve: s.present(strings),
    },
  ),
  packaging: asset<Owner.PackagingAsset>()("packaging"),
  message: asset<Owner.MessageAsset>()(
    "message",
    { messageText: nonBlank },
    {
      claimType: s.enum(MESSAGE_CLAIM_TYPES),
      supportingProof: s.string(),
      intendedAudience: s.string(),
    },
  ),
  brand: asset<Owner.BrandAsset>()(
    "brand",
    {},
    { proposition: s.string(), targetUser: s.string() },
  ),
  pain: asset<Owner.PainAsset>()("pain"),
});
const testedAssets = objectOf<Owner.TestedAssetBlock>()(
  {
    mode: s.enum(["none", "all_assets"]),
    required: s.boolean,
    assetTypes: s.array(s.enum(testedAssetTypes)),
    assets: s.array(testedAsset),
    validation: validationState,
  },
  {
    sharedContext: objectOf<Owner.TestedAssetSharedContext>()({
      schemaVersion: s.literal("tested-asset-shared-context.v1"),
      text: nonBlank,
      provenance: objectOf<Owner.TestedAssetSharedContextProvenance>()({
        kind: s.literal("literal_source_segments"),
        segments: s.array(objectOf<Owner.TestedAssetSharedContextSourceSegment>()({ sourceId: nonBlank, text: nonBlank })),
      }),
    }),
    metadata: objectOf<Owner.TestedAssetBlockMetadata>()(
      {},
      {
        intakeShape: s.enum(["short_claims", "long_form"]),
        setSimilarity: s.enum(["variants", "dissimilar"]),
        respondsToSituation: s.boolean,
        messageIntent: s.string(),
        messageChannel: s.string(),
      },
    ),
  },
);

/**
 * Closed structural contract for the tested-asset block when it is projected
 * outside the accepted draft (for example into current canonical SurveyJSON).
 * Keeping this as a view over the accepted-artifact schema prevents the
 * compiler ingress from growing a second, weaker asset contract.
 */
export function acceptedTestedAssetBlockStructureIssues(
  value: unknown,
  path = "testedAssets",
): Array<QuestionnaireValidationIssue> {
  const result: ContractResult = { issues: [] };
  validate(testedAssets, value, path, result);
  return result.issues;
}

/**
 * The product price contract, the one owner of a valid price (F18 cross
 * review): an amount above 0 in one of `PRODUCT_PRICE_CURRENCIES`. It is the
 * schema the tested-asset block above validates a product's `price` with, so
 * the builder's card, the applier (every writer: the builder's Save, the chat,
 * PLAN's added assets) and the write gate agree on what a price is.
 */
export function productPriceStructureIssues(
  value: unknown,
  path = "price",
): Array<QuestionnaireValidationIssue> {
  const result: ContractResult = { issues: [] };
  validate(productPrice, value, path, result);
  return result.issues;
}

/**
 * A typed price read from a value that crossed a boundary (a model's output,
 * a stored draft): its validated copy, with `billingPeriod` only when set, or
 * null when the price contract refuses it. The one reader every Nest site that
 * forwards a price uses, so none rebuilds the shape by hand.
 */
export function readProductPrice(value: unknown): Owner.ProductAssetPrice | null {
  if (productPriceStructureIssues(value).length > 0) {
    return null;
  }
  const price = value as Owner.ProductAssetPrice;
  return {
    amount: price.amount,
    currencyCode: price.currencyCode,
    ...(price.billingPeriod ? { billingPeriod: price.billingPeriod } : {}),
  };
}

const exposureMark = objectOf<Owner.QuestionnaireExposureMark>()({
  stableKey: nullable(nonBlank),
  source: s.enum(["agent", "user"]),
  updatedAt: nonBlank,
});
const exposureBoundary = objectOf<Owner.QuestionnaireExposureBoundary>()({
  schemaVersion: s.literal("questionnaire-exposure-boundary.v1"),
  reveal: nullable(exposureMark),
  sharedView: nullable(exposureMark),
});
const canonicalQuestionMapEntry = objectOf<Owner.CanonicalQuestionMapEntry>()(
  {
    questionId: nonBlank,
    exposureZone: s.enum(["pre", "lane", "shared_view"]),
    stableKey: nonBlank,
    canonicalType: s.enum(questionTypes),
    surveyJsName: nonBlank,
    metricIds: strings,
    qualitativeAnalysisIds: strings,
    slideIds: strings,
  },
  {
    unit: s.enum(unitIds),
    assetRefs: s.array(assetMentionRef),
    choiceAssetRefs: s.array(
      objectOf<Owner.CanonicalChoiceAssetRefEntry>()({
        optionValue: nonBlank,
        optionLabel: nonBlank,
        assetRefs: s.array(assetMentionRef),
      }),
    ),
    assetBinding: assetQuestionBinding,
    x_dt_post_reveal: s.boolean,
    x_dt_tags: strings,
    priceRole: s.enum(["too_cheap", "cheap", "expensive", "too_expensive"]),
    ordered: s.present(s.boolean),
    // The report's copy of the element's title and description (F5′).
    reportTitle: nonBlank,
    reportDescription: nonBlank,
    // The held-back facts a question is answered without, the ones it tells,
    // and the ones told before it (wave D, D2): the report's before/after reads.
    answeredWithout: strings,
    reveals: strings,
    toldBefore: strings,
  },
);
const canonicalAssetContextItem = objectOf<Owner.CanonicalAssetContextItem>()(
  {
    assetId: nonBlank,
    assetStableKey: nonBlank,
    assetType: s.enum(testedAssetTypes),
    title: nonBlank,
    description: s.string(),
  },
  {
    respondentCopy: nonBlank,
    twinFacingLabel: nonBlank,
    media: s.array(media),
    role: s.enum(["stimulus", "catalog"]),
  },
);
const canonicalAssetContext = objectOf<Owner.CanonicalAssetContext>()(
  {
    schemaVersion: s.literal("canonical-asset-context.v1"),
    items: s.array(canonicalAssetContextItem),
  },
  { sharedContext: s.string() },
);
const canonicalAssetImageSlice = objectOf<Owner.CanonicalAssetImageSlice>()(
  { index: s.number(true), role: nonBlank },
  {
    bucket: s.string(),
    objectKey: s.string(),
    gcsUri: s.string(),
    publicUrl: s.string(),
    mimeType: s.string(),
    altText: s.string(),
  },
);
const canonicalAssetImageContextItem = objectOf<Owner.CanonicalAssetImageContextItem>()(
  {
    assetId: nonBlank,
    assetStableKey: nonBlank,
    title: nonBlank,
    mediaId: nonBlank,
    kind: s.literal("image"),
  },
  {
    bucket: s.string(),
    objectKey: s.string(),
    gcsUri: s.string(),
    publicUrl: s.string(),
    mimeType: s.string(),
    altText: s.string(),
    // The owner defaults absent slices to none; it refuses a null list.
    slices: s.present(s.array(canonicalAssetImageSlice)),
  },
);
const canonicalAssetImageContext = objectOf<Owner.CanonicalAssetImageContext>()({
  schemaVersion: s.literal("canonical-asset-image-context.v1"),
  items: s.array(canonicalAssetImageContextItem),
});
const canonicalVideoStoryboardImage = objectOf<Owner.CanonicalVideoStoryboardImage>()({
  url: nonBlank,
  frames: s.array(
    objectOf<Owner.CanonicalVideoStoryboardFrame>()({ number: s.number(true), seconds: s.number() }),
  ),
});
const canonicalAssetVideoContextItem = objectOf<Owner.CanonicalAssetVideoContextItem>()(
  {
    assetId: nonBlank,
    assetStableKey: nonBlank,
    title: nonBlank,
    mediaId: nonBlank,
    kind: s.literal("video"),
    brief: s.string(),
    storyboard: s.array(canonicalVideoStoryboardImage),
  },
  {
    videoUrl: nullable(s.string()),
    posterUrl: nullable(s.string()),
    analysisUrl: nullable(s.string()),
    durationSeconds: nullable(s.number()),
  },
);
const canonicalAssetVideoContext = objectOf<Owner.CanonicalAssetVideoContext>()({
  schemaVersion: s.literal("canonical-asset-video-context.v1"),
  items: s.array(canonicalAssetVideoContextItem),
});
const canonicalSurveyConfig = objectOf<Owner.CanonicalSurveyConfig>()(
  {
    schemaVersion: s.literal("canonical-survey-config.v1"),
    design: s.enum(["comparative", "true_monadic", "twin_monadic"]),
    stimulusIdentityPolicy: s.literal("canonical-assets.v1"),
    questionnaireDraftId: nonBlank,
    registryVersion: s.literal("questionnaire-registry.v1"),
    questionMap: s.record(canonicalQuestionMapEntry),
  },
  {
    questionnaireRevision: nullable(s.number(true)),
    exposureBoundary,
    assetContext: canonicalAssetContext,
    assetImageContext: canonicalAssetImageContext,
    assetVideoContext: canonicalAssetVideoContext,
    videoIntelligenceMode: s.enum(["standard", "extra"]),
    audienceType: nonBlank,
    metricPlanKey: nonBlank,
    slidePlanKey: nonBlank,
    testedAssetsKey: nonBlank,
    contentLanguage: nonBlank,
  },
);

const canonicalElementMetadata = s.object(
  {
    responseGuidance: nonBlank,
    exposureZone: s.enum(["pre", "lane", "shared_view"]),
  },
  {
    followUpToQuestionIds: strings,
    unit: s.enum(unitIds),
    currencyCode: s.string(),
    priceRole: s.enum(["too_cheap", "cheap", "expensive", "too_expensive"]),
    durationUnit: s.enum(DURATION_UNITS),
    polarity: s.enum(RATING_POLARITIES),
    // Retired boolean polarity of a survey compiled before `polarity`.
    higherIsBetter: s.boolean,
    measures: s.array(matrixMeasure),
    allowNone: s.boolean,
    assetRefs: s.array(assetMentionRef),
    choiceAssetRefs: s.array(
      s.object({
        optionValue: nonBlank,
        optionLabel: nonBlank,
        assetRefs: s.array(assetMentionRef),
      }),
    ),
    assetBinding: assetQuestionBinding,
    awarenessEntities: strings,
    // The held-back facts this question tells (wave D, D2): the engine keeps
    // its answer in every later question's history (`SessionHistory`).
    reveals: strings,
    // Each stimulus lane's copy of a lane question that @mentions a stimulus,
    // by asset id (compiler `laneMentionCopy`, engine `scope_per_asset_to_page`).
    laneCopy: s.record(
      s.object(
        { title: nonBlank },
        {
          description: s.string(),
          // Retired (F5′): the compiler no longer stamps that a lane's answers
          // name a stimulus; respondent prose names every asset by the
          // registry's one rule. Surveys compiled before it still carry the
          // key, so it stays readable; nothing reads it.
          namesStimulus: s.boolean,
        },
      ),
    ),
    // Retired (F5′): the element-level "copy names a stimulus" stamp, as on
    // `laneCopy` above. Readable for surveys compiled before it; unread.
    namesStimulus: s.boolean,
  },
);
const canonicalChoiceMetadata = s.object({
  assetRefs: s.array(assetMentionRef),
});
const canonicalRowAsset = s.object(
  {
    assetId: nonBlank,
    assetStableKey: nonBlank,
    assetType: s.enum(testedAssetTypes),
    title: nonBlank,
  },
  // Retired (C02-4): the compiler no longer writes a row asset's description,
  // which carried generated text into the respondent payload. Launched surveys
  // compiled before it are immutable and still carry it, so it stays readable.
  { description: s.string() },
);

/** Current compiler-owned config shape; semantic identity joins stay caller-owned. */
export function acceptedCanonicalSurveyConfigStructureIssues(
  value: unknown,
  path = "_canonicalConfig",
): Array<QuestionnaireValidationIssue> {
  const result: ContractResult = { issues: [] };
  validate(canonicalSurveyConfig, value, path, result);
  return result.issues;
}

/**
 * Current compiler-owned element metadata, including nested choice/row
 * projections. SurveyJS vendor fields deliberately remain open.
 */
export function acceptedCanonicalSurveyElementStructureIssues(
  value: unknown,
  canonicalType: unknown,
  path: string,
): Array<QuestionnaireValidationIssue> {
  const result: ContractResult = { issues: [] };
  if (!isRecord(value)) {
    fail(result, path, "Expected a SurveyJS element object.");
    return result.issues;
  }
  validate(
    canonicalElementMetadata,
    value._canonical,
    `${path}._canonical`,
    result,
  );
  if (isRecord(value._canonical)) {
    const metadata = value._canonical;
    if (
      canonicalType === "continuous" &&
      !CONTINUOUS_UNIT_IDS.includes(metadata.unit as never)
    ) {
      fail(result, `${path}._canonical.unit`, "Expected a continuous unit.");
    }
    if (
      canonicalType === "rating" &&
      (!RATING_UNIT_IDS.includes(metadata.unit as never) ||
        (metadata.polarity === undefined &&
          typeof metadata.higherIsBetter !== "boolean"))
    ) {
      fail(
        result,
        `${path}._canonical`,
        "A rating requires its rating unit and declared polarity.",
      );
    }
    if (
      canonicalType === "open_end" &&
      !Array.isArray(metadata.followUpToQuestionIds)
    ) {
      fail(
        result,
        `${path}._canonical.followUpToQuestionIds`,
        "An open end requires its follow-up identity list.",
      );
    }
    if (
      canonicalType === "matrix_rating" &&
      (!Array.isArray(metadata.measures) || metadata.measures.length === 0)
    ) {
      fail(
        result,
        `${path}._canonical.measures`,
        "A rating matrix requires at least one measure.",
      );
    }
    if (
      canonicalType === "matrix_categorical" &&
      typeof metadata.allowNone !== "boolean"
    ) {
      fail(
        result,
        `${path}._canonical.allowNone`,
        "A categorical matrix requires allowNone.",
      );
    }
  }
  if (Array.isArray(value.choices)) {
    value.choices.forEach((choice, index) => {
      if (isRecord(choice) && choice._canonical !== undefined) {
        validate(
          canonicalChoiceMetadata,
          choice._canonical,
          `${path}.choices[${index}]._canonical`,
          result,
        );
      }
    });
  }
  if (Array.isArray(value.rows)) {
    value.rows.forEach((row, index) => {
      if (!isRecord(row)) return;
      const rowPath = `${path}.rows[${index}]`;
      if (
        row._canonicalAssetId !== undefined ||
        row._canonicalAsset !== undefined
      ) {
        validate(
          nonBlank,
          row._canonicalAssetId,
          `${rowPath}._canonicalAssetId`,
          result,
        );
        validate(
          canonicalRowAsset,
          row._canonicalAsset,
          `${rowPath}._canonicalAsset`,
          result,
        );
      }
    });
  }
  return result.issues;
}

const metricSource = s.discriminated("kind", {
  question: objectOf<Owner.QuestionMetricSource>()({ kind: s.literal("question"), questionId: nonBlank }),
  matrix_measure: objectOf<Owner.MatrixMeasureMetricSource>()({
    kind: s.literal("matrix_measure"),
    questionId: nonBlank,
    measureId: nonBlank,
  }),
  matrix_row: objectOf<Owner.MatrixRowMetricSource>()({
    kind: s.literal("matrix_row"),
    questionId: nonBlank,
    rowId: nonBlank,
  }),
  asset_measure: objectOf<Owner.AssetMeasureMetricSource>()(
    {
      kind: s.literal("asset_measure"),
      questionId: nonBlank,
      assetType: s.enum(testedAssetTypes),
    },
    { measureId: nonBlank },
  ),
});
const metric = objectOf<Owner.MetricDefinition>()(
  {
    id: nonBlank,
    questionId: nonBlank,
    stableKey: nonBlank,
    source: metricSource,
    metricKind: s.enum(METRIC_KINDS),
    display: objectOf<Owner.MetricDisplaySpec>()(
      {
        label: nonBlank,
        nativeUnitLabel: nonBlank,
        valueFormat: s.enum([
          "mean_out_of_scale",
          "percent",
          "currency",
          "count",
          "duration",
          "probability",
          "index",
          "text",
        ]),
        decimals: s.number(true),
        showNormalizedValue: s.boolean,
      },
      { shortLabel: nonBlank },
    ),
    extraction: objectOf<Owner.MetricExtractionSpec>()(
      {
        method: s.enum([
          "mean",
          "median",
          "distribution",
          "multi_select_share",
          "rating_secondary_metrics",
          "continuous_summary",
          "matrix_rating_summary",
          "matrix_categorical_distribution",
          "qualitative_theme_package",
          "ranking_weighted_score",
          "paired_lift",
        ]),
      },
      {
        ignoreValues: s.array(
          s.custom((value, path, result) => {
            if (
              typeof value !== "string" &&
              (typeof value !== "number" || !Number.isFinite(value))
            ) {
              fail(result, path, "Expected a string or finite number.");
            }
          }),
        ),
        thresholds: s.record(
          s.custom((value, path, result) => {
            validate(
              Array.isArray(value) ? s.array(s.number()) : s.number(),
              value,
              path,
              result,
            );
          }),
        ),
      },
    ),
    segmentable: s.boolean,
  },
  {
    unit: s.enum(unitIds),
    normalization: objectOf<Owner.NormalizationSpec>()({
      method: s.enum(["linear_bounds", "binary", "observed_bounds"]),
      min: s.number(),
      max: s.number(),
      direction: s.enum(RATING_POLARITIES),
      outputRange: s.literal("0_1"),
    }),
    baselineQuestionId: nonBlank,
  },
);
const compositeMetric = s.discriminated("kind", {
  winner_composite: objectOf<Owner.WinnerCompositeDefinition>()({
    id: nonBlank,
    label: nonBlank,
    kind: s.literal("winner_composite"),
    appliesTo: s.enum(testedAssetTypes),
    components: s.array(
      objectOf<Owner.CompositeMetricComponent>()({
        metricId: nonBlank,
        weight: s.number(),
        direction: s.enum(["higher_is_better", "lower_is_better"]),
      }),
    ),
    output: objectOf<Owner.CompositeMetricOutput>()(
      {
        normalizedRange: s.literal("0_100"),
        displayLabel: nonBlank,
      },
      {
        valueFormat: s.enum(["percent", "index"]),
      },
    ),
  }),
  // C15a: the podium slot ranked by the study verdict. A marker only.
  study_verdict: objectOf<Owner.StudyVerdictCompositeDefinition>()({
    id: nonBlank,
    kind: s.literal("study_verdict"),
    appliesTo: s.enum(testedAssetTypes),
  }),
});
const qualitativeAnalysis = objectOf<Owner.QualitativeAnalysisDefinition>()(
  {
    id: nonBlank,
    questionId: nonBlank,
    stableKey: nonBlank,
    source: metricSource,
    targetThemeCount: objectOf<Owner.QualitativeTargetThemeCount>()({ min: s.literal(3), max: s.literal(5) }),
    requiredOutputs: objectOf<Owner.QualitativeRequiredOutputs>()({
      themes: s.literal(true),
      quotesPerTheme: s.literal(true),
      keywordsPerTheme: s.literal(true),
    }),
  },
  {
    classifier: objectOf<Owner.OpenEndClassifierSpec>()({
      kind: s.enum([
        "comprehension",
        "objection_severity",
        "affect",
        "improvement_themes",
      ]),
      metricKind: nonBlank,
    }),
  },
);
const metricPlan = objectOf<Owner.MetricPlan, "segmentationPlan">()(
  {
    schemaVersion: s.literal("metric-plan.v1"),
    registryVersion: s.literal("questionnaire-registry.v1"),
    questionnaireDraftId: nonBlank,
    metrics: s.array(metric),
    compositeMetrics: s.array(compositeMetric),
    qualitativeAnalyses: s.array(qualitativeAnalysis),
  },
  { segmentationPlan: s.unknown },
);

const dataRequirements = objectOf<Owner.SlideDataRequirements>()({
  requiresQuantMetrics: s.boolean,
  requiresQualThemes: s.boolean,
  requiresQuotes: s.boolean,
  requiresKeywords: s.boolean,
  requiresAssets: s.boolean,
  requiresCompositeWinner: s.boolean,
  requiresQuadrantAxes: s.boolean,
});
// `storyRole` and `cutRequestIds` are refused: the analysis stamps them on
// its own copy of the plan, and no draft writer sets them.
const slideItem = objectOf<Owner.SlidePlanItem, never, "storyRole" | "cutRequestIds">()(
  {
    id: nonBlank,
    slideType: s.enum(CANONICAL_SLIDE_TYPES),
    role: s.enum([
      "opener",
      "summary",
      "question_evidence",
      "comparison",
      "recommendation",
      "outro",
    ]),
    title: nonBlank,
    questionIds: strings,
    metricIds: strings,
    qualitativeAnalysisIds: strings,
    compositeMetricIds: strings,
    dataRequirements,
  },
  { assetType: s.enum(testedAssetTypes) },
);
const slidePlan = objectOf<Owner.SlidePlan>()({
  schemaVersion: s.literal("slide-plan.v1"),
  registryVersion: s.literal("questionnaire-registry.v1"),
  questionnaireDraftId: nonBlank,
  dynamicLength: s.literal(true),
  requiredSlides: objectOf<Owner.RequiredDeckSlides>()({
    cover: slideItem,
    executiveSummary: slideItem,
    recommendations: slideItem,
    outro: slideItem,
  }),
  questionSlides: s.array(
    objectOf<Owner.QuestionSlideMapping>()({ questionId: nonBlank, slideItemIds: strings }),
  ),
  comparisonSlides: s.array(s.object({}, {}, true)),
  ordering: s.array(slideItem),
});

const reportRequirement = s.discriminated("kind", {
  sources: objectOf<Owner.ReportSourcesProjectionRequirement>()({
    kind: s.literal("sources"),
  }),
  declared_binding: objectOf<Owner.ReportDeclaredBindingProjectionRequirement>()({
    kind: s.literal("declared_binding"),
    binding: s.enum(["quadrantAxes", "liftBaseline", "comparison", "priceCurve"]),
  }),
});
const reportAxis = objectOf<Owner.ReportAxis>()({ questionId: nonBlank }, { measureId: nonBlank });
const quadrantAxes = objectOf<Owner.ReportQuadrantAxes>()(
  { x: reportAxis, y: reportAxis },
  {
    cornerLabels: s.custom((value, path, result) => {
      if (!Array.isArray(value) || value.length !== 4) {
        fail(result, path, "Expected exactly four corner labels.");
        return;
      }
      validate(strings, value, path, result);
    }),
  },
);
const liftBaseline = objectOf<Owner.ReportLiftBaseline>()({
  baselineQuestionId: nonBlank,
  againstQuestionId: nonBlank,
});
const comparison = objectOf<Owner.ReportComparisonTable>()({
  questionId: nonBlank,
  maxMeasures: s.number(true),
});
const priceCurve = objectOf<Owner.ReportPriceCurve>()(
  {
    questionIdByRole: s.object(
      {},
      {
        too_cheap: nonBlank,
        cheap: nonBlank,
        expensive: nonBlank,
        too_expensive: nonBlank,
      },
    ),
  },
  // D8. Optional on both sides of the wire: a plan stamped before the
  // reference read was typed carries no key at all, and one that resolved no
  // reference carries null. Neither is a stale plan.
  { referenceQuestionId: nullable(nonBlank) },
);
const reportBindings = objectOf<Owner.ReportingProjectionBindings>()(
  {},
  { quadrantAxes, liftBaseline, comparison, priceCurve },
);
const sourceRef = s.discriminated("kind", {
  recipe_module: objectOf<Owner.RecipeModuleReportSource>()({
    kind: s.literal("recipe_module"),
    moduleId: nonBlank,
  }),
  custom_topic: objectOf<Owner.CustomTopicReportSource>()({
    kind: s.literal("custom_topic"),
    topicKey: nonBlank,
  }),
});
const sourceBinding = objectOf<Owner.ReportSourceBinding>()(
  { stableKey: nonBlank, questionType: s.enum(questionTypes) },
  {
    sourceRef,
    reportInputModuleId: nonBlank,
    moduleId: nonBlank,
    questionId: nonBlank,
    sourceKind: s.enum(["recipe", "reporting_target"]),
  },
);
const reportOption = objectOf<Owner.ReportOptionPlan>()(
  {
    shape: s.enum([...REPORT_CARD_SHAPES]),
    fromModules: strings,
    status: s.enum(["planned", "absent", "unjoined"]),
  },
  {
    candidateModules: strings,
    basis: s.enum(["chosen", "distribution", "scored", "pictures"]),
    item: s.literal("focal"),
    requirement: reportRequirement,
    sourceQuestionIds: strings,
    reason: s.string(),
  },
);
const reportSlotFields = {
  chosenIndex: s.number(true),
  whenAbsent: s.string(),
  candidateWhenAbsent: s.string(),
  candidateModules: strings,
  selectorIntent: s.string(),
} satisfies Fields;
const reportSlot = objectOf<Owner.ReportSlotPlan>()(
  { options: s.array(reportOption) },
  reportSlotFields,
);
const kpiSlot = objectOf<Owner.ReportKpiSlotPlan, "rows" | "secondary">()(
  {},
  {
    options: s.present(s.array(reportOption)),
    // Read compatibility for stamps made before the canonical `options` key.
    rows: s.array(reportOption),
    secondary: s.array(reportOption),
    ...reportSlotFields,
  },
);
const reportCard = objectOf<Owner.ReportCardPlan>()(
  {
    card: s.enum([
      "lift",
      "diagnostics",
      "podium",
      "quadrant",
      "kpi_rings",
      "comparison",
      "price_curve",
      "quotes",
    ]),
    available: s.boolean,
  },
  {
    fromModules: strings,
    candidateModules: strings,
    whenAbsent: s.string(),
    candidateWhenAbsent: s.string(),
    requirement: reportRequirement,
    sourceQuestionIds: strings,
    absentReason: s.string(),
  },
);
// Retired: a copy of the primary metric that nothing read. The commit
// projection (`projectReportingPlan`) drops it, but persisted drafts stamped
// before its removal carry it on the plan and its projectionSpec and must
// remain readable, matching Python's ReportingPlan.
const retiredHeadlineMetric = { headlineMetricId: s.unknown };
const reportingPlan = objectOf<Owner.ReportingPlan, "headlineMetricId">()(
  { cards: s.array(reportCard) },
  {
    projectionSpec: objectOf<Owner.ReportingProjectionSpec, "headlineMetricId">()(
      {
        schemaVersion: s.literal("reporting-projection.v1"),
        bindings: reportBindings,
      },
      retiredHeadlineMetric,
    ),
    sourceBindings: s.array(sourceBinding),
    ...retiredHeadlineMetric,
    slots: objectOf<Owner.ReportSlotsPlan>()({ kpi: kpiSlot, plot: reportSlot, quotes: reportSlot }),
    quadrantAxes,
    liftBaseline,
    comparison,
    priceCurve,
    moduleTitles: s.present(s.record(nonBlank)),
    moduleQualStoryRoles: s.record(
      s.enum(["first_impression", "friction", "what_works"]),
    ),
    moduleLeadStatistics: s.record(
      s.enum(["favourable_share", "modal_share", "rank_index", "median"]),
    ),
  },
);

const studyGoals = objectOf<Owner.StudyGoals>()(
  {
    decision: s.string(),
    learn: s.array(s.string()),
    source: s.enum(["inferred", "confirmed", "edited"]),
  },
  {
    decisionIsFallback: s.boolean,
    framing: s.string(),
    stated_versions: s.number(true),
    stated_segments: s.array(s.string()),
  },
);

const authoredObjective = objectOf<Owner.AuthoredObjective>()(
  {
    decision: s.string(),
    learn: s.array(s.string()),
  },
  { title: s.string() },
);

const designRecommendation = s.refine(
  objectOf<Owner.DesignRecommendation>()({
    exposure: nullable(s.enum(["monadic", "comparison"])),
    baseline: nullable(s.boolean),
    rationale: s.string(),
  }),
  (value, path, result) => {
    if (isRecord(value) && value.exposure === null && value.baseline === null) {
      fail(
        result,
        path,
        "Design recommendation must declare exposure, baseline, or both.",
      );
    }
  },
);

const decisionModes = s.refine(
  s.array(s.enum(["absolute", "relative", "diagnostic"])),
  (value, path, result) => {
    if (!Array.isArray(value)) return;
    if (value.length < 1 || value.length > 3) {
      fail(result, path, "Decision modes must contain between 1 and 3 items.");
    } else if (new Set(value).size !== value.length) {
      fail(result, path, "Decision modes must not contain duplicates.");
    }
  },
);

const designBrief = objectOf<Owner.DesignBrief>()(
  {
    method: s.string(),
    primaryMetric: s.string(),
    keyMetrics: s.array(s.string()),
    caveats: s.array(s.string()),
    novelty: s.boolean,
    classification: s.enum(["routed", "reframed", "authored", "custom"]),
    objectiveText: s.string(),
  },
  {
    authoredObjective,
    incrementality_required: s.boolean,
    sellingPriceRequirement: s.enum([
      "required",
      "not_required_for_thresholds",
    ]),
    secondary_objectives: s.array(s.string()),
    exposure: s.enum(["monadic", "comparison"]),
    adDesignPreference: s.enum(["monadic", "comparison"]),
    decisionModes,
    setSimilarity: s.enum(["variants", "dissimilar"]),
    benchmark: s.enum(["benchmark_asset", "user_target", "none"]),
    situationalContextPresent: s.boolean,
    namedAction: s.string(),
    situationalContext: s.string(),
    designRecommendation,
  },
);

const studyEvidence = s.custom((value, path, result) => {
  if (!isStudyEvidenceContract(value)) {
    fail(result, path, "Expected a closed StudyEvidenceContract.");
  }
});

// `brandName` is nullable in Python's PlanBrandFacts, while a present
// `suggestCompetitors` is a StrictBool, never null.
const brandFacts = objectOf<Owner.PlanBrandFacts>()(
  {},
  {
    brandName: nonBlank,
    competitorNames: s.array(nonBlank),
    suggestCompetitors: s.present(s.boolean),
  },
);

// The "Study brand" record (`QuestionnaireDraft.studyBrand`, plan Decision 5):
// the user's edit, whose null name is an explicit "no brand", or the
// provenance PLAN declared for the recorded brand, which names none.
const studyBrand = s.union(
  {
    StudySpecStudyBrandEdit: objectOf<Owner.StudySpecStudyBrandEdit>()({
      brandName: nullable(nonBlank),
      source: s.literal("user_edit"),
    }),
    StudySpecStudyBrandProvenance: objectOf<Owner.StudySpecStudyBrandProvenance>()({
      source: s.enum(STUDY_BRAND_SOURCES.filter((source) => source !== "user_edit")),
    }),
  },
  (value) =>
    isRecord(value) && value.source === "user_edit"
      ? "StudySpecStudyBrandEdit"
      : "StudySpecStudyBrandProvenance",
);

const draft = objectOf<Owner.QuestionnaireDraft, "assemblyWarnings" | "segmentation">()(
  {
    schemaVersion: s.literal("questionnaire-draft.v1"),
    registryVersion: s.literal("questionnaire-registry.v1"),
    id: nonBlank,
    mode: s.literal("custom"),
    status: s.enum([
      "collecting_problem",
      "asking_clarifying_questions",
      "draft_ready",
      "editing",
      "assets_required",
      "ready_for_review",
      "launched",
      "archived",
      "duplicating",
      "duplicate_failed",
    ]),
    // A questionnaire title is an internal study label. It may be cleared;
    // question titles remain non-blank because respondents must read them.
    title: s.string(),
    // The first text a writer gave the study; empty while none has (S27).
    // Nothing stands in for it.
    problemStatement: s.string(),
    assumptions: s.array(
      objectOf<Owner.Assumption>()({
        id: nonBlank,
        source: s.enum(["chat", "system", "user_confirmed"]),
        text: nonBlank,
        affects: s.array(
          s.enum(["questions", "assets", "metrics", "slides", "audience"]),
        ),
        createdAt: nonBlank,
      }),
    ),
    // `{}` while no audience is chosen: the launch asks for one.
    audience: objectOf<Owner.AudienceSelectionRef>()(
      {},
      {
        populationId: nonBlank,
        storageId: s.string(),
        populationFilter: s.unknown,
        estimatedRespondentCount: s.number(true),
        // Accepted before any pod stores it. Persistence is gated by
        // DT_PLANNED_SAMPLE_SIZE_PERSISTENCE so a previous release, whose
        // audience object is closed, never compiles a draft that carries it.
        plannedSampleSize: s.number(true),
        populationName: s.string(),
        populationDescription: s.string(),
        audienceType: s.string(),
        contextText: s.string(),
        aiDescription: s.string(),
        selectedSegments: s.array(
          objectOf<Owner.SelectedSegment>()(
            { id: nonBlank, label: s.string() },
            { conditions: s.present(s.array(s.record(s.unknown))) },
          ),
        ),
        // The filter in the filter catalog's labels (C09-4).
        populationFilterLabels: s.array(
          objectOf<Owner.AudienceFilterLabel>()(
            { attribute: nonBlank, values: s.array(s.string()) },
            {
              options: s.array(s.string()),
              optionsMatch: s.enum(["any", "all"]),
            },
          ),
        ),
      },
    ),
    researchQuestions: s.array(question),
    testedAssets,
    metricPlan,
    slidePlan,
    compilerHints: objectOf<Owner.CompilerHints, "lpMode">()(
      { respondentFlow: s.literal("linear") },
      {
        surveyJsNamePrefix: s.string(),
        preferredQuestionsPerPage: s.number(true),
        // Retired and undeclared on `CompilerHints`: tolerated on a draft
        // stored before the normaliser dropped it on write.
        lpMode: s.boolean,
      },
    ),
    validation: validationState,
    createdAt: nonBlank,
    updatedAt: nonBlank,
  },
  {
    description: s.string(),
    contentLanguage: s.string(),
    studyGoals,
    studyEvidence,
    designBrief,
    adDesignPreference: s.enum(["monadic", "comparison"]),
    // Nest reads it through `isValidPlanCoverageRecord`, which takes a
    // malformed record as absent; the owner refuses one, so the gate does too.
    planCoverage: objectOf<Owner.QuestionnairePlanCoverageRecord>()({
      schemaVersion: s.literal("questionnaire-plan-coverage.v1"),
      recipeId: nonBlank,
      recipeVersion: nonBlank,
      justifiedQuestionCount: s.number(true),
      moduleUniverse: strings,
      selectedModules: strings,
      excludedModules: s.array(
        objectOf<Owner.QuestionnairePlanCoverageExclusion>()({
          moduleId: nonBlank,
          reason: s.string(),
        }),
      ),
    }),
    // Plan Stage 3: the StudySpec's shape is its JSON Schema's, checked with
    // the spec itself by the write gate's `study_spec_*` codes. Its version is
    // checked here: `studySpecOf` reads any other as no spec at all, and the
    // owner refuses it.
    studySpec: s.object(
      { schemaVersion: s.literal(STUDY_SPEC_SCHEMA_VERSION) },
      {},
      true,
    ),
    exposureBoundary,
    // Historical advisory metadata. Current normalization intentionally drops
    // it, but persisted drafts carrying it must remain readable.
    assemblyWarnings: s.unknown,
    // Historical drafts remain readable, matching Python's draft model.
    segmentation: s.unknown,
    // PLAN projects this root into Python's closed PlanBrandFacts model.
    brandFacts,
    studyBrand,
    reportingPlan,
    // The latest PLAN's material gaps (C22-4).
    materialGaps: s.array(
      objectOf<Owner.MaterialGap>()({ assetStableKey: nonBlank, missingFact: nonBlank }),
    ),
    // The audience filter digest the latest PLAN saw (C09-4).
    planAudienceFilterDigest: s.string(),
  },
);

/**
 * The rule trees, read by `scripts/contracts/dump-accepted-artifact-rules.ts`
 * into `packages/shared-types/generated/accepted-artifact-rules.json`, which
 * the owner parity test compares with the Pydantic models.
 */
export type AcceptedArtifactRule = Schema;
export const ACCEPTED_ARTIFACT_RULES: Readonly<Record<string, Schema>> = {
  QuestionnaireDraft: draft,
  CanonicalSurveyConfig: canonicalSurveyConfig,
};

/**
 * Shared structural owner for the DesignBrief shape.
 *
 * The objective-routing receiver applies its tighter response-size limits
 * after this check. Accepted persisted state uses this structural half because
 * its normalizer has deliberately different bounds and supports fact-only
 * briefs whose required strings are empty.
 */
export function acceptedDesignBriefStructureIssues(
  value: unknown,
  path = "designBrief",
): Array<QuestionnaireValidationIssue> {
  const result: ContractResult = { issues: [] };
  validate(designBrief, value, path, result);
  return result.issues;
}

/**
 * The one stimulus ref of a question shaped as an exact subject, or null.
 *
 * The question shows that one asset and nothing else: its binding is `none`,
 * or the row mirror of a grid whose rows are a list respondents are never
 * shown (`isShownAssetFamily`: pains, brands), read against the subject (pain
 * v5's solution fit); its one `stimulus` ref is `manual`; every other ref is
 * one of those rows (`matrix_row`). Rows of a shown family carry every subject
 * themselves, so they never take one.
 */
function exactSubjectRef(question: JsonRecord): JsonRecord | null {
  const binding = isRecord(question.assetBinding) ? question.assetBinding : null;
  const types = Array.isArray(binding?.assetTypes) ? binding.assetTypes : null;
  const response = isRecord(question.response) ? question.response : null;
  const rows = isRecord(response?.rows) ? response.rows : null;
  const listFamily =
    binding?.mode === "all_assets" &&
    binding.rowSource === "tested_assets" &&
    types?.length === 1 &&
    !isShownAssetFamily(types[0]) &&
    rows?.source === "tested_assets" &&
    rows.assetType === types[0]
      ? types[0]
      : null;
  if (!(binding?.mode === "none" && types?.length === 0) && listFamily === null) {
    return null;
  }
  const refs = Array.isArray(question.assetRefs) ? question.assetRefs : [];
  const stimulus = refs.filter((ref) => isRecord(ref) && ref.role === "stimulus");
  const ref = stimulus.length === 1 ? (stimulus[0] as JsonRecord) : null;
  const othersAreRows = refs.every(
    (other) =>
      other === ref ||
      (isRecord(other) &&
        listFamily !== null &&
        other.role === "matrix_row" &&
        other.assetType === listFamily),
  );
  return ref?.source === "manual" && othersAreRows ? ref : null;
}

/**
 * The exact-subject shape (I-5), its one owner: this gate, AUTHOR's result
 * (`author-result.validator.ts`) and the mention rule
 * (`hasCanonicalExactSubjectContract`) read it, and the applier's retype rule
 * (`shapeKeepsExactSubject`) reads its family predicate.
 *
 * A v3 attribution's `subjectAssetStableKey` names the one tested asset the
 * question shows, in the shape `exactSubjectRef` states; an answer, category
 * or fixed row names no other asset. The reverse holds too: a v3 question of
 * that shape keeps its subject attribution. Typed structure only; `path` is
 * the question's.
 */
export function exactSubjectShapeIssues(
  question: unknown,
  path: string,
): Array<{ path: string; message: string }> {
  if (!isRecord(question)) return [];
  const attribution = isRecord(question.planAttribution)
    ? question.planAttribution
    : null;
  const subjectKey = attribution?.subjectAssetStableKey;
  const v3 = attribution?.schemaVersion === "question-plan-attribution.v3";
  const subjectPath = `${path}.planAttribution.subjectAssetStableKey`;
  const ref = exactSubjectRef(question);
  if (subjectKey == null) {
    return v3 &&
      typeof ref?.assetStableKey === "string" &&
      ref.assetStableKey.trim().length > 0
      ? [
          {
            path: subjectPath,
            message:
              "A v3 non-expanding single-subject question must retain its exact asset subject attribution.",
          },
        ]
      : [];
  }
  const issues: Array<{ path: string; message: string }> = [];
  if (!v3 || ref?.assetStableKey !== subjectKey) {
    issues.push({
      path: subjectPath,
      message:
        "An exact asset subject requires a v3 attribution, exactly one matching explicit stimulus ref, and a binding that shows nothing else: none, or the rows of a list respondents are never shown.",
    });
  }
  const response = isRecord(question.response) ? question.response : {};
  const rows = isRecord(response.rows) ? response.rows : null;
  const nestedSurfaces: Array<{ path: string; entries: Array<unknown> }> = [
    ...(Array.isArray(response.options)
      ? [{ path: `${path}.response.options`, entries: response.options }]
      : []),
    ...(Array.isArray(response.categories)
      ? [{ path: `${path}.response.categories`, entries: response.categories }]
      : []),
    ...(rows?.source === "fixed" && Array.isArray(rows.rows)
      ? [{ path: `${path}.response.rows.rows`, entries: rows.rows }]
      : []),
  ];
  nestedSurfaces.forEach((surface) => {
    surface.entries.forEach((nested, nestedIndex) => {
      if (!isRecord(nested) || !Array.isArray(nested.assetRefs)) return;
      nested.assetRefs.forEach((nestedRef, refIndex) => {
        if (
          !isRecord(nestedRef) ||
          nestedRef.assetStableKey !== subjectKey ||
          nestedRef.assetId !== ref?.assetId ||
          nestedRef.assetType !== ref?.assetType
        ) {
          issues.push({
            path: `${surface.path}[${nestedIndex}].assetRefs[${refIndex}]`,
            message:
              "An exact scalar subject cannot expose a sibling asset through an option, category, or fixed matrix row.",
          });
        }
      });
    });
  });
  return issues;
}

/**
 * Structural half of the accepted PLAN/REPLAN bundle contract.
 *
 * Manual and planless WIP drafts deliberately do not call this validator.
 * Accepted generated state must be safe for both the compiler and Python's
 * strict canonical-analysis models. Semantic and graph rules remain owned by
 * `validateQuestionnaireDraftPayload` and the persistence accepted-state gate.
 */
export function acceptedQuestionnaireArtifactStructureIssues(
  value: unknown,
): Array<QuestionnaireValidationIssue> {
  const result: ContractResult = { issues: [] };
  validate(draft, value, "", result);

  // The response discriminator is also the question's declared type. Keeping
  // the cross-field equality here prevents a structurally valid response union
  // from contradicting the compiler branch selected by `question.type`.
  if (isRecord(value) && Array.isArray(value.researchQuestions)) {
    value.researchQuestions.forEach((entry, index) => {
      if (!isRecord(entry) || !isRecord(entry.response)) return;
      if (entry.type !== entry.response.kind) {
        fail(
          result,
          `researchQuestions[${index}].response.kind`,
          "Response kind must match the declared question type.",
        );
      }
      exactSubjectShapeIssues(entry, `researchQuestions[${index}]`).forEach(
        (issue) => fail(result, issue.path, issue.message),
      );
    });

    const priceRoles = [
      "too_cheap",
      "cheap",
      "expensive",
      "too_expensive",
    ] as const;
    const roleBearing: Array<{ index: number; role: unknown; type: unknown }> =
      [];
    value.researchQuestions.forEach((entry, index) => {
      if (!isRecord(entry) || !isRecord(entry.planAttribution)) return;
      const role = entry.planAttribution.priceRole;
      if (role == null) return;
      roleBearing.push({ index, role, type: entry.type });
    });
    if (roleBearing.length > 0) {
      const roles = new Set(roleBearing.map((entry) => entry.role));
      const hasNonContinuous = roleBearing.some(
        (entry) => entry.type !== "continuous",
      );
      if (
        hasNonContinuous ||
        roleBearing.length !== priceRoles.length ||
        roles.size !== priceRoles.length ||
        priceRoles.some((role) => !roles.has(role))
      ) {
        fail(
          result,
          "researchQuestions.planAttribution.priceRole",
          "A complete Van Westendorp threshold method requires each price role exactly once on a continuous question.",
        );
      }
    }
  }
  return result.issues;
}
